import React, { createContext, useContext, useReducer, useEffect, useMemo } from 'react';
import type { ReactNode } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { setCurrentState, setCurrentTourId, setSessionData, setFailedCheckpoints } from '../../../../store/ALCSlice';
import { useMsal } from "@azure/msal-react";

export const ALC_STATES = {
    INIT_PRODUCTION: "INIT_PRODUCTION",
    PENDING_QA_ACCEPTANCE: "PENDING_QA_ACCEPTANCE",
    QA_ACCEPTED: "QA_ACCEPTED",
    QA_CHECKLIST: "QA_CHECKLIST",
    COMPLETED_PASS: "COMPLETED_PASS",
    PRODUCTION_ACTION: "PRODUCTION_ACTION",
    QA_REVERIFYING: "QA_REVERIFYING",
    SUMMARY: "SUMMARY"
} as const;

export type ALC_STATES = typeof ALC_STATES[keyof typeof ALC_STATES];

export const ALC_ROLES = {
    PRODUCTION: "PRODUCTION",
    QUALITY: "QUALITY",
    PRODUCT: "PRODUCT"
} as const;

export type ALC_ROLES = typeof ALC_ROLES[keyof typeof ALC_ROLES];

export interface ALCSessionData {
    plant: string;
    line: string;
    shift: string;
    productionExecutive: string;
    previousProduct: string;
    newProduct: string;
    assignedQaEmail: string;
    assignedQaName: string;
    escalationEmails: string[];
    requestTime: string | null;
    rawRecord?: any;
}

export interface ALCState {
    currentState: ALC_STATES;
    userRole: ALC_ROLES;
    currentTourId: string | null;
    sessionData: ALCSessionData | null;
    failedCheckpoints: any[];
    isLoading: boolean;
    isReadOnly: boolean;
    isPreviousDay: boolean;
    userAreas: string[];
}

export type ALCAction =
    | { type: 'SET_STATE'; payload: ALC_STATES }
    | { type: 'SET_ROLE'; payload: ALC_ROLES }
    | { type: 'SET_TOUR_ID'; payload: string }
    | { type: 'SET_SESSION_DATA'; payload: ALCSessionData }
    | { type: 'SET_FAILED_CHECKPOINTS'; payload: any[] }
    | { type: 'SET_LOADING'; payload: boolean }
    | { type: 'SET_READ_ONLY'; payload: boolean }
    | { type: 'SET_PREVIOUS_DAY'; payload: boolean }
    | { type: 'SET_USER_AREAS'; payload: string[] };

// We will dynamically initialize this from Redux now
const defaultInitialState: ALCState = {
    currentState: ALC_STATES.INIT_PRODUCTION,
    userRole: ALC_ROLES.PRODUCTION,
    currentTourId: null,
    sessionData: null,
    failedCheckpoints: [],
    isLoading: false,
    isReadOnly: false,
    isPreviousDay: false,
    userAreas: []
};

function alcReducer(state: ALCState, action: ALCAction): ALCState {
    switch (action.type) {
        case 'SET_STATE':
            return { ...state, currentState: action.payload };
        case 'SET_ROLE':
            return { ...state, userRole: action.payload };
        case 'SET_TOUR_ID':
            return { ...state, currentTourId: action.payload };
        case 'SET_SESSION_DATA':
            return { ...state, sessionData: action.payload };
        case 'SET_FAILED_CHECKPOINTS':
            return { ...state, failedCheckpoints: action.payload };
        case 'SET_LOADING':
            return { ...state, isLoading: action.payload };
        case 'SET_READ_ONLY':
            return { ...state, isReadOnly: action.payload };
        case 'SET_PREVIOUS_DAY':
            return { ...state, isPreviousDay: action.payload };
        case 'SET_USER_AREAS':
            return { ...state, userAreas: action.payload };
        default:
            return state;
    }
}

interface ALCContextType {
    state: ALCState;
    dispatch: React.Dispatch<ALCAction>;
    isProductionUser: boolean;
    isQaUser: boolean;
    isProductUser: boolean;
}

const ALCContext = createContext<ALCContextType | undefined>(undefined);

export const ALCProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const alcReduxState = useSelector((globalState: any) => globalState.ALC);
    
    // Initialize from Redux to support offline reload
    const [state, localDispatch] = useReducer(alcReducer, {
        ...defaultInitialState,
        currentState: (alcReduxState?.currentState as ALC_STATES) || ALC_STATES.INIT_PRODUCTION,
        currentTourId: alcReduxState?.currentTourId || null,
        sessionData: alcReduxState?.sessionData || null,
        failedCheckpoints: alcReduxState?.failedCheckpoints || [],
    });
    
    const reduxDispatch = useDispatch();

    // Wrap dispatch to sync with Redux
    const dispatch = useMemo(() => (action: ALCAction) => {
        localDispatch(action);
        
        switch (action.type) {
            case 'SET_STATE':
                reduxDispatch(setCurrentState(action.payload));
                break;
            case 'SET_TOUR_ID':
                reduxDispatch(setCurrentTourId(action.payload));
                break;
            case 'SET_SESSION_DATA':
                reduxDispatch(setSessionData(action.payload));
                break;
            case 'SET_FAILED_CHECKPOINTS':
                reduxDispatch(setFailedCheckpoints(action.payload));
                break;
        }
    }, [reduxDispatch]);
    
    // Resolve user role based on Redux employeeDetails and QA Config when provider mounts
    const employeeDetails = useSelector((globalState: any) => globalState.planTour?.employeeDetails);
    const qaList = useSelector((globalState: any) => globalState.ALC?.config) || [];
    const { accounts } = useMsal();
    
    useEffect(() => {
        const currentUser = accounts[0]?.name || "";
        const currentEmail = accounts[0]?.username || "";
        
        // Check if user is explicitly in the QA Config or is an Escalation Manager
        const isQAConfigUser = qaList.some((c: any) => 
            c.ConfigType?.includes('QA') && (
                c.AssignedUser?.results?.some((u: any) => 
                    u.Title?.toLowerCase() === currentUser.toLowerCase() || 
                    (u.EMail && u.EMail.toLowerCase() === currentEmail.toLowerCase()) ||
                    u.Title?.toLowerCase().includes(currentUser.toLowerCase().split(' ')[0])
                ) || 
                c.EscalationManager?.results?.some((m: any) => 
                    m.Title?.toLowerCase() === currentUser.toLowerCase() || 
                    (m.EMail && m.EMail.toLowerCase() === currentEmail.toLowerCase()) ||
                    m.Title?.toLowerCase().includes(currentUser.toLowerCase().split(' ')[0])
                )
            )
        );

        if (isQAConfigUser) {
            dispatch({ type: 'SET_ROLE', payload: ALC_ROLES.QUALITY });
        } else if (employeeDetails) {
            const department = employeeDetails.departmentName || "";
            const roleName = employeeDetails.roleName || "";
            
            if (department.toLowerCase().includes("quality")) {
                dispatch({ type: 'SET_ROLE', payload: ALC_ROLES.QUALITY });
            } else if (roleName.toLowerCase().includes("manager") || roleName.toLowerCase().includes("incharge")) {
                dispatch({ type: 'SET_ROLE', payload: ALC_ROLES.PRODUCT });
            } else {
                dispatch({ type: 'SET_ROLE', payload: ALC_ROLES.PRODUCTION });
            }
        } else {
             // Fallback if no employee details loaded yet
             dispatch({ type: 'SET_ROLE', payload: ALC_ROLES.PRODUCTION });
        }
    }, [employeeDetails, qaList, accounts, dispatch]);

    const isProductionUser = state.userRole === ALC_ROLES.PRODUCTION;
    const isQaUser = state.userRole === ALC_ROLES.QUALITY;
    const isProductUser = state.userRole === ALC_ROLES.PRODUCT;

    return (
        <ALCContext.Provider value={{ state, dispatch, isProductionUser, isQaUser, isProductUser }}>
            {children}
        </ALCContext.Provider>
    );
};

export const useALCContext = () => {
    const context = useContext(ALCContext);
    if (!context) {
        throw new Error('useALCContext must be used within an ALCProvider');
    }
    return context;
};
